import { copy } from '../../copy/investor-copy'
import { formatShortDate } from '../following/dates'
import { RelCard } from './rel-card'
import type { CardActions, Relationship, RelationshipUpdate } from './types'

const TEXT_LINK = 'text-xs text-muted-foreground transition-colors hover:text-foreground'

const KIND_COPY = {
  deck: 'card.since.kind.deck',
  document: 'card.since.kind.document',
  update: 'card.since.kind.update',
  session: 'card.since.kind.session',
} as const

/** "12 Oct 2026 · New document": the small line over an entry. */
export function entryHeading(entry: RelationshipUpdate): string {
  return copy('card.since.entry', {
    date: formatShortDate(entry.date),
    kind: copy(KIND_COPY[entry.kind]),
  })
}

/** An alert shows the founder's words, in quotes; anything else shows its title. */
export function entryText(entry: RelationshipUpdate): string {
  return entry.quote ? `“${entry.quote}”` : entry.title
}

/** "Since you followed": what the founder published after the follow started. Hidden unless following. */
export function SinceCard({ data, actions }: { data: Relationship; actions: CardActions }) {
  const { follow } = data
  if (!follow || (data.state !== 'ACTIVE' && data.state !== 'CONDITION_MET')) return null
  return (
    <RelCard
      title={copy('card.since.title')}
      right={
        <span className="text-xs text-muted-foreground">
          {copy('card.since.from', { date: formatShortDate(follow.followedAt) })}
        </span>
      }
    >
      {data.sinceFollowed.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">{copy('card.since.empty')}</p>
      ) : (
        <ul className="mt-1 divide-y divide-border">
          {data.sinceFollowed.map((entry) => (
            <li key={entry.id} className="flex gap-3 py-2.5">
              <span
                className={`mt-1.5 size-1.5 shrink-0 rounded-full ${entry.isNew ? 'bg-primary' : 'bg-muted-foreground'}`}
              />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground">{entryHeading(entry)}</p>
                <p className="mt-0.5 text-sm text-foreground/90">{entryText(entry)}</p>
                <button
                  type="button"
                  className={`mt-1 inline-block ${TEXT_LINK}`}
                  onClick={() => actions.onViewSource(entry)}
                >
                  {copy('card.since.viewSource')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </RelCard>
  )
}

/** "Latest from {startup}": for someone who does not follow, the last published changes. */
export function LatestUpdatesCard({ data }: { data: Relationship }) {
  if (data.latestUpdates.length === 0) return null
  return (
    <RelCard title={copy('card.latest.title', { startup: data.startup.name })}>
      <ul className="mt-1 divide-y divide-border">
        {data.latestUpdates.map((entry) => (
          <li key={entry.id} className="flex justify-between gap-3 py-2.5 text-sm">
            <span className="min-w-0 text-foreground/90">{entry.title}</span>
            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
              {formatShortDate(entry.date)}
            </span>
          </li>
        ))}
      </ul>
    </RelCard>
  )
}
